// Permission-based route guard.
//
// Blocks rendering of `children` (and redirects to `redirectTo` with a toast)
// when the current user lacks the required permission(s) on the active
// organization. Complements the sidebar/menu-level role gating in UserMenu by
// catching direct URL navigation.
//
// NOTE: Role-based access uses the existing `RoleGuard` component; this
// component is intentionally permission-based so it matches the backend's
// Permission.* checks 1:1 and can be read alongside the auth-service guards.

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions, Permission } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';

interface RequirePermissionProps {
  /** One permission (all required) or an array (all required). */
  permission: Permission | Permission[];
  /** Where to send the user if the check fails. Defaults to `/dashboard`. */
  redirectTo?: string;
  children: React.ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  redirectTo = '/dashboard',
  children,
}) => {
  const { hasPermission, currentRole } = usePermissions();
  const { addToast } = useToast();

  const required = Array.isArray(permission) ? permission : [permission];
  const allowed = required.every((p) => hasPermission(p));
  const shouldRedirect = currentRole !== null && !allowed;

  useEffect(() => {
    if (shouldRedirect) {
      addToast('You do not have permission to access that page', 'error');
    }
  }, [shouldRedirect, addToast]);

  // Still resolving the organization role — render nothing to avoid a
  // redirect flash before OrganizationContext populates `user_role`.
  if (currentRole === null) return null;
  if (!allowed) return <Navigate to={redirectTo} replace />;

  return <>{children}</>;
};

export default RequirePermission;
